import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { catalogApi, type CatalogProduct, type KeyPattern, type KeyPatternInput } from '@/api/catalogApi'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { Field, NativeSelect } from '@/components/common/Field'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { FormError, TextAction, tdClass, thClass } from '@/components/admin/catalog/CatalogShared'
import { useCatalogMutation } from '@/hooks/useCatalog'
import { EMPTY_PATTERN_FORM, NORMALIZERS, patternPayload, patternToForm, testPattern, validatePattern, type KeyPatternForm } from '@/lib/catalogForms'
import { cn } from '@/lib/utils'

/** Record-key patterns of a product, with a live regex tester in the dialog. */
export function KeyPatternsPanel({ product }: { product: CatalogProduct }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<KeyPattern | 'new' | null>(null)
  const [deleting, setDeleting] = useState<KeyPattern | null>(null)
  const save = useCatalogMutation(({ id, input }: { id?: string; input: KeyPatternInput }) =>
    id ? catalogApi.updateKeyPattern(product.id, id, input) : catalogApi.addKeyPattern(product.id, input),
  )
  const remove = useCatalogMutation((id: string) => catalogApi.removeKeyPattern(product.id, id))

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setEditing('new')}>{t('catalog.patterns.add')}</Button>
      </div>
      {product.keyPatterns.length === 0 ? (
        <p className="py-6 text-meta text-ink-muted">{t('catalog.patterns.empty')}</p>
      ) : (
        <table className="w-full table-fixed border-collapse">
          <thead className="bg-surface-subtle">
            <tr>
              <th className={`${thClass} w-28`}>{t('catalog.patterns.cols.type')}</th>
              <th className={thClass}>{t('catalog.patterns.cols.regex')}</th>
              <th className={`${thClass} w-[200px]`}>{t('catalog.patterns.cols.normalizer')}</th>
              <th className={thClass}>{t('catalog.patterns.cols.url')}</th>
              <th className={`${thClass} w-32`} />
            </tr>
          </thead>
          <tbody>
            {product.keyPatterns.map((p) => (
              <tr key={p.id} className="border-b border-line">
                <td className={`${tdClass} font-mono font-medium text-ink-strong`}>{p.type}</td>
                <td className={`${tdClass} break-all font-mono text-label`}>{p.regex}</td>
                <td className={tdClass}>{t(`catalog.normalizers.${p.normalizer}`, { defaultValue: p.normalizer })}</td>
                <td className={`${tdClass} break-all font-mono text-label`}>{p.urlTemplate ?? '—'}</td>
                <td className={tdClass}>
                  <div className="flex justify-end gap-3">
                    <TextAction onClick={() => setEditing(p)}>{t('common.edit')}</TextAction>
                    <TextAction danger onClick={() => setDeleting(p)}>
                      {t('common.delete')}
                    </TextAction>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing ? (
        <PatternDialog
          pattern={editing === 'new' ? null : editing}
          existingTypes={product.keyPatterns.filter((p) => editing === 'new' || p.id !== editing.id).map((p) => p.type)}
          pending={save.isPending}
          error={save.error}
          onClose={() => {
            setEditing(null)
            save.reset()
          }}
          onSave={(input) => save.mutate({ id: editing === 'new' ? undefined : editing.id, input }, { onSuccess: () => setEditing(null) })}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        destructive
        title={t('catalog.patterns.deleteTitle', { name: deleting?.type ?? '' })}
        body={t('catalog.patterns.deleteBody')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : null}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null)
            remove.reset()
          }
        }}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
      />
    </div>
  )
}

function PatternDialog({
  pattern,
  existingTypes,
  pending,
  error,
  onClose,
  onSave,
}: {
  pattern: KeyPattern | null
  existingTypes: string[]
  pending: boolean
  error: unknown
  onClose: () => void
  onSave: (input: KeyPatternInput) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<KeyPatternForm>(() => (pattern ? patternToForm(pattern) : EMPTY_PATTERN_FORM))
  const [tryKey, setTryKey] = useState(() => pattern?.sample ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const err = (k: string) => (errors[k] ? t(errors[k]) : null)
  const probe = tryKey || form.sample
  const result = testPattern(form, probe)

  function submit(e: FormEvent) {
    e.preventDefault()
    const found = validatePattern(form, existingTypes) as Record<string, string>
    setErrors(found)
    if (Object.keys(found).length === 0) onSave(patternPayload(form))
  }

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{pattern ? t('catalog.patterns.editTitle') : t('catalog.patterns.createTitle')}</DialogTitle>
        </DialogHeader>
        <form className="grid grid-cols-2 gap-4" onSubmit={submit} noValidate>
          <Field id="kp-type" label={t('catalog.patterns.type')} error={err('type')} help={t('catalog.patterns.typeHelp')}>
            <Input id="kp-type" className="font-mono" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
          </Field>
          <Field id="kp-norm" label={t('catalog.patterns.normalizer')}>
            <NativeSelect
              id="kp-norm"
              value={form.normalizer}
              onChange={(e) => setForm({ ...form, normalizer: e.target.value as KeyPatternForm['normalizer'] })}
            >
              {NORMALIZERS.map((n) => (
                <option key={n} value={n}>
                  {t(`catalog.normalizers.${n}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="kp-regex" label={t('catalog.patterns.regex')} error={err('regex')} className="col-span-2">
            <Input id="kp-regex" className="font-mono" value={form.regex} onChange={(e) => setForm({ ...form, regex: e.target.value })} />
          </Field>
          <Field id="kp-sample" label={t('catalog.patterns.sample')} error={err('sample')}>
            <Input id="kp-sample" className="font-mono" value={form.sample} onChange={(e) => setForm({ ...form, sample: e.target.value })} />
          </Field>
          <Field id="kp-url" label={t('catalog.patterns.urlTemplate')} error={err('urlTemplate')} help={t('catalog.patterns.urlHelp')}>
            <Input id="kp-url" className="font-mono" value={form.urlTemplate} onChange={(e) => setForm({ ...form, urlTemplate: e.target.value })} />
          </Field>

          <section aria-label={t('catalog.patterns.tester')} className="col-span-2 flex flex-col gap-3 rounded-md border border-line bg-surface-subtle p-4">
            <p className="text-eyebrow font-medium uppercase text-ink-muted">{t('catalog.patterns.tester')}</p>
            <Field id="kp-try" label={t('catalog.patterns.testKey')}>
              <Input id="kp-try" className="font-mono" placeholder={form.sample} value={tryKey} onChange={(e) => setTryKey(e.target.value)} />
            </Field>
            <dl className="grid grid-cols-[180px_1fr] gap-x-4 gap-y-2 text-meta" data-testid="pattern-tester">
              <dt className="text-ink-muted">{t('catalog.patterns.cols.regex')}</dt>
              <dd
                className={cn(
                  'font-medium',
                  result.matches === true ? 'text-status-resolved-fg' : 'text-status-escalated-fg',
                )}
              >
                {result.regexError
                  ? t('catalog.patterns.badRegex', { message: result.regexError })
                  : result.matches
                    ? t('catalog.patterns.matches')
                    : t('catalog.patterns.noMatch')}
              </dd>
              <dt className="text-ink-muted">{t('catalog.patterns.normalized')}</dt>
              <dd className="break-all font-mono text-ink-strong">{result.normalized || '—'}</dd>
              <dt className="text-ink-muted">{t('catalog.patterns.url')}</dt>
              <dd className="break-all font-mono text-ink-body">{result.url ?? t('catalog.patterns.noUrl')}</dd>
            </dl>
          </section>

          <FormError error={error} />
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
